import React, { useEffect, useRef, useState } from 'react'

import User from '../models/User'

import { GlobalHeader } from '../components/GlobalHeader/GlobalHeader'
import { useSelector, useDispatch } from 'react-redux'

import {
  closeMessageModal,
  getUnreadMessageCount,
  getAllMessages,
  setSelectedMessageId,
  getIsMessageModalOpen,
  fetchMessages
} from 'redux/reducers/messages'
import {
  fetchReqTokenAndUser,
  fetchUser,
  getUser
} from 'redux/reducers/users'

import { fetchToasts } from 'redux/reducers/toasts'
import { LanguagePreference } from 'models/LanguagePreference'

import {
  getIsSessionWarningModalOpen, setSessionWarningModal
} from 'redux/reducers/modal'

import { MessageCenterModal } from '../components/MessageCenter/MessageCenterModal/MessageCenterModal'
import { TimeoutWarningModal } from 'components/BrowserApi/TimeoutLogic/TimeoutWarningModal'
import { useFlags } from 'launchdarkly-react-client-sdk'

interface HeaderProps {
  currentLangProps: LanguagePreference
  activeNavSection?: string;
  languageToggleLink: string;
  loginUrl: string;
  logoutUrl: string;
  enableLiveChat: boolean
  disableLogin: boolean
  enableMessageCenter: boolean
  liveChatViewpage?: string
  loadAndDisplayToasts: boolean
}

const Header: React.FC<HeaderProps> = (
  {
    currentLangProps,
    activeNavSection,
    languageToggleLink,
    loginUrl,
    logoutUrl,
    disableLogin,
    enableLiveChat = true,
    enableMessageCenter = false,
    liveChatViewpage,
    loadAndDisplayToasts
  }
) => {
  const dispatch = useDispatch()
  const user = useSelector(getUser)
  const isSessionWarningModalOpen = useSelector(getIsSessionWarningModalOpen)
  const flags = useFlags()

  // Fetch user
  useEffect(() => {
    // TODO: Retired feature flag after prod deployment
    if (flags?.useOnlySlsJwtForAuthByDefault) {
      dispatch(fetchUser())
    } else {
      dispatch(fetchReqTokenAndUser())
    }
  }, [dispatch, flags])

  // Fetch toasts - if they haven't been set, the user exists, and the page should load and display toasts
  const toastsFetched = useRef(false)
  useEffect(() => {
    if (
      !toastsFetched.current &&
      user &&
      loadAndDisplayToasts
    ) {
      toastsFetched.current = true
      dispatch(fetchToasts(currentLangProps))
    }
  }, [dispatch, user, loadAndDisplayToasts, currentLangProps])

  // Fetch messages - if they haven't been set and the user exists
  useEffect(() => {
    if (
      user &&
      enableMessageCenter
    ) {
      dispatch(fetchMessages(currentLangProps))
    }
  }, [dispatch, user, enableMessageCenter, currentLangProps])

  useOpenMessageFromQueryParam(user) // Deprecate this hook in favor of a reducer, dispatch to modal update

  const unreadCount = useSelector(getUnreadMessageCount)
  const isMessageModalOpen = useSelector(getIsMessageModalOpen)

  return (
    <>
      {isMessageModalOpen ? <MessageCenterModal onClose={() => dispatch(closeMessageModal())} /> : ''}
      {
        isSessionWarningModalOpen
          ? (
          <TimeoutWarningModal onClose={() => {
            dispatch(setSessionWarningModal({ isSessionWarningModalOpen: false }))
          }}
          />)
          : ''
      }
      <GlobalHeader
        activeNavSection={activeNavSection}
        user={user}
        loginUrl={loginUrl}
        logoutUrl={logoutUrl}
        disableLogin={disableLogin}
        languageToggleLink={languageToggleLink}
        enableLiveChat={enableLiveChat}
        enableMessageCenter={enableMessageCenter}
        liveChatViewpage={liveChatViewpage}
        unreadCount={unreadCount}
      />
    </>
  )
}

const useOpenMessageFromQueryParam = (user?: User) => {
  const [hasModalBeenOpened, setHasModalBeenOpened] = useState(false)

  const dispatch = useDispatch()
  const messages = useSelector(getAllMessages)

  useEffect(() => {
    const locationSearch = window.location.search
    const params = new URLSearchParams(locationSearch)
    const messageID = params.get('mc_mid')

    if (user && messages.length && !hasModalBeenOpened && messageID) {
      const doesMessageIDExist = messages.some(message => message.guid === messageID)
      const newMessageID = doesMessageIDExist ? messageID : ''

      setHasModalBeenOpened(true)
      dispatch(setSelectedMessageId(newMessageID))
    }
  }, [user, messages, hasModalBeenOpened, dispatch])
}

export default Header
